import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Banknote,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  Loader2,
} from 'lucide-react'
import { apiFetch, ApiClientError } from '@/lib/api-client'
import { useAuthStore } from '@/lib/auth-store'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { PerformanceScopeBanner } from '@/components/PerformanceScopeBanner'
import { usePerformanceScope } from '@/lib/use-performance-scope'

// ─── Types ───────────────────────────────────────────────────────────────────

interface PaymentSummary {
  totalPaidPence: number
  totalPaidCount: number
  totalPendingPence: number
  totalPendingCount: number
  thisMonthPaidPence: number
  thisMonthPaidCount: number
}

interface CrewPayment {
  id: string
  crewProfileId: string
  amountPence: number
  feePence: number
  netPence: number
  currency: string
  description: string
  status: string
  paidAt: string | null
  createdAt: string
  crewProfile?: {
    id: string
    displayName: string
    primaryRole: string
  }
}

interface PayableCrew {
  id: string
  displayName: string
  primaryRole: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatMoney(pence: number, currency = 'GBP'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
  }).format(pence / 100)
}

function roleLabel(role: string): string {
  return role.replace(/_/g, ' ')
}

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  processing: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function PaymentsPage() {
  const artistId = useAuthStore((s) => s.user?.artistId)
  const scope = usePerformanceScope()
  const prefix = artistId ? `/artists/${artistId}/payments` : null
  const scopeQs = scope.toQueryString()

  return (
    <div>
      <PageHeader
        title="Crew Payments"
        subtitle="Pay crew members directly via Stripe Connect."
      />
      <PerformanceScopeBanner />
      {prefix ? (
        <div className="space-y-8">
          <SummaryCards prefix={prefix} scopeQs={scopeQs} scopeKey={scope.queryKeySuffix} />
          <PayCrewForm prefix={prefix} />
          <PaymentHistory prefix={prefix} scopeQs={scopeQs} scopeKey={scope.queryKeySuffix} />
        </div>
      ) : (
        <p className="text-sm text-ovation-muted">
          Select an artist to manage payments.
        </p>
      )}
    </div>
  )
}

// ─── Summary Cards ───────────────────────────────────────────────────────────

function SummaryCards({
  prefix,
  scopeQs,
  scopeKey,
}: {
  prefix: string
  scopeQs: string
  scopeKey: readonly string[]
}) {
  const { data, isLoading } = useQuery<PaymentSummary, ApiClientError>({
    queryKey: ['payment-summary', prefix, ...scopeKey],
    queryFn: () => apiFetch<PaymentSummary>(`${prefix}/summary${scopeQs}`),
  })

  if (isLoading || !data) {
    return (
      <div className="grid gap-4 sm:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-24 animate-pulse rounded-lg border border-ovation-ink/10 bg-white"
          />
        ))}
      </div>
    )
  }

  const cards = [
    {
      label: 'Total paid',
      value: formatMoney(data.totalPaidPence),
      sub: `${data.totalPaidCount} payment${data.totalPaidCount !== 1 ? 's' : ''}`,
      icon: CheckCircle,
      colour: 'text-green-600',
    },
    {
      label: 'Pending',
      value: formatMoney(data.totalPendingPence),
      sub: `${data.totalPendingCount} payment${data.totalPendingCount !== 1 ? 's' : ''}`,
      icon: Clock,
      colour: 'text-amber-600',
    },
    {
      label: 'This month',
      value: formatMoney(data.thisMonthPaidPence),
      sub: `${data.thisMonthPaidCount} payment${data.thisMonthPaidCount !== 1 ? 's' : ''}`,
      icon: Banknote,
      colour: 'text-ovation-ink',
    },
  ]

  return (
    <div className="grid gap-4 sm:grid-cols-3">
      {cards.map((c) => (
        <div
          key={c.label}
          className="rounded-lg border border-ovation-ink/10 bg-white p-5"
        >
          <div className="mb-2 flex items-center gap-2">
            <c.icon className={`h-4 w-4 ${c.colour}`} />
            <span className="text-xs uppercase tracking-wider text-ovation-muted">
              {c.label}
            </span>
          </div>
          <div className="text-2xl font-semibold text-ovation-ink">{c.value}</div>
          <div className="mt-1 text-xs text-ovation-muted">{c.sub}</div>
        </div>
      ))}
    </div>
  )
}

// ─── Pay Crew Form ───────────────────────────────────────────────────────────

function PayCrewForm({ prefix }: { prefix: string }) {
  const queryClient = useQueryClient()

  const { data: payableCrew } = useQuery<PayableCrew[], ApiClientError>({
    queryKey: ['payable-crew', prefix],
    queryFn: () => apiFetch<PayableCrew[]>(`${prefix}/payable-crew`),
  })

  const [crewProfileId, setCrewProfileId] = useState('')
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    setSubmitting(true)
    try {
      const amountPence = Math.round(parseFloat(amount) * 100)
      if (Number.isNaN(amountPence) || amountPence <= 0) {
        throw new Error('Enter a valid amount')
      }

      await apiFetch(`${prefix}`, {
        method: 'POST',
        body: { crewProfileId, amountPence, description },
      })

      setSuccess('Payment created. Click "Send" in the table below to process it.')
      setCrewProfileId('')
      setAmount('')
      setDescription('')
      queryClient.invalidateQueries({ queryKey: ['payments', prefix] })
      queryClient.invalidateQueries({ queryKey: ['payment-summary', prefix] })
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Unexpected error',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const hasCrew = (payableCrew ?? []).length > 0

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
      <h2 className="mb-4 text-xs uppercase tracking-wider text-ovation-muted">
        Pay crew member
      </h2>

      {!hasCrew ? (
        <p className="text-sm text-ovation-muted">
          No crew members with connected Stripe accounts yet. Crew members must
          connect their Stripe account from their profile before they can receive
          payments.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label
                htmlFor="pay-crew"
                className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted"
              >
                Crew member
              </label>
              <select
                id="pay-crew"
                value={crewProfileId}
                onChange={(e) => setCrewProfileId(e.target.value)}
                required
                className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
              >
                <option value="">Select crew...</option>
                {(payableCrew ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName} ({roleLabel(c.primaryRole)})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="pay-amount"
                className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted"
              >
                Amount
              </label>
              <input
                id="pay-amount"
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                placeholder="0.00"
                className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
              />
            </div>
            <div>
              <label
                htmlFor="pay-desc"
                className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted"
              >
                Description
              </label>
              <input
                id="pay-desc"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                placeholder="e.g. Show fee - Manchester 15 Apr"
                className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          {success && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-1.5 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            <Banknote className="h-4 w-4" />
            {submitting ? 'Creating...' : 'Create payment'}
          </button>
        </form>
      )}
    </div>
  )
}

// ─── Payment History ─────────────────────────────────────────────────────────

function PaymentHistory({
  prefix,
  scopeQs,
  scopeKey,
}: {
  prefix: string
  scopeQs: string
  scopeKey: readonly string[]
}) {
  const queryClient = useQueryClient()
  const [processingId, setProcessingId] = useState<string | null>(null)

  const { data, isLoading } = useQuery<CrewPayment[], ApiClientError>({
    queryKey: ['payments', prefix, ...scopeKey],
    queryFn: async () => {
      const qs = scopeQs ? scopeQs.replace('?', '') : ''
      const suffix = qs ? `&${qs}` : ''
      const res = await apiFetch<CrewPayment[]>(`${prefix}?limit=50${suffix}`)
      return res
    },
  })

  async function handleProcess(paymentId: string) {
    if (!confirm('Send this payment via Stripe? This cannot be undone.')) return
    setProcessingId(paymentId)
    try {
      await apiFetch(`${prefix}/${paymentId}/process`, { method: 'POST' })
      queryClient.invalidateQueries({ queryKey: ['payments', prefix] })
      queryClient.invalidateQueries({ queryKey: ['payment-summary', prefix] })
    } catch (err) {
      alert(
        err instanceof ApiClientError
          ? err.message
          : 'Failed to process payment',
      )
    } finally {
      setProcessingId(null)
    }
  }

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
      <h2 className="mb-4 text-xs uppercase tracking-wider text-ovation-muted">
        Payment history
      </h2>

      <AsyncBoundary isLoading={isLoading} error={null}>
        {(data ?? []).length === 0 ? (
          <p className="text-sm text-ovation-muted">No payments yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ovation-ink/10 text-left text-xs uppercase tracking-wider text-ovation-muted">
                  <th className="pb-2 pr-4">Crew member</th>
                  <th className="pb-2 pr-4">Description</th>
                  <th className="pb-2 pr-4 text-right">Amount</th>
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2 pr-4">Date</th>
                  <th className="pb-2" />
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-ovation-ink/5 last:border-0"
                  >
                    <td className="py-2.5 pr-4">
                      <div className="font-medium">
                        {p.crewProfile?.displayName ?? 'Unknown'}
                      </div>
                      {p.crewProfile?.primaryRole && (
                        <div className="text-xs text-ovation-muted">
                          {roleLabel(p.crewProfile.primaryRole)}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 pr-4">{p.description}</td>
                    <td className="py-2.5 pr-4 text-right font-medium">
                      {formatMoney(p.netPence, p.currency)}
                    </td>
                    <td className="py-2.5 pr-4">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[p.status] ?? 'bg-gray-100 text-gray-700'}`}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 text-xs text-ovation-muted">
                      {p.paidAt
                        ? new Date(p.paidAt).toLocaleDateString()
                        : new Date(p.createdAt).toLocaleDateString()}
                    </td>
                    <td className="py-2.5">
                      {p.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => handleProcess(p.id)}
                          disabled={processingId === p.id}
                          className="flex items-center gap-1 rounded-md bg-ovation-ink px-2.5 py-1 text-xs font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
                        >
                          {processingId === p.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Send className="h-3 w-3" />
                          )}
                          Send
                        </button>
                      )}
                      {p.status === 'failed' && (
                        <span className="flex items-center gap-1 text-xs text-red-600">
                          <XCircle className="h-3 w-3" /> Failed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncBoundary>
    </div>
  )
}
